/* eslint no-var: 0, vars-on-top: 0, one-var: 0 */
import React from 'react';
import formMixin from 'mixins/user-form-mixin';
import assign from 'object-assign';
import validationMixin from 'mixins/validationMixin';
import CustomersAPI from 'utils/CustomersAPIUtils';
import { withTranslation } from 'react-i18next';
import SessionStore from 'stores/SessionStore';

import {
  Alert,
  FormControl,
  Radio,
  FormGroup,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';
import update from 'immutability-helper';

import { capitalize } from 'underscore.string';
import moment from 'moment/moment';
import EditableSection from 'components/v2/EditableSection';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import { getValidFormatMMDDYYYY } from 'common/helpers/dateUtils';
import { updateStateForField } from '../user-form-utils';
import AddressForm from '../address-form';

const BUSINESS_TYPES = {
  COMPANY: 'company',
  INDIVIDUAL: 'individual',
};

const BUSINESS_TYPE_SLUGS = {
  [BUSINESS_TYPES.COMPANY]: 'BUSINESS_TYPE_COMPANY',
  [BUSINESS_TYPES.INDIVIDUAL]: 'BUSINESS_TYPE_INDIVIDUAL',
};

let defaults = {
  errors: [],
  loading: false,
  type: BUSINESS_TYPES.INDIVIDUAL,
  first_name: '',
  last_name: '',
  personal_id_number: '',
  dob: {
    month: '',
    day: '',
    year: '',
  },
  business_name: '',
  business_tax_id: '',
  address: {
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    name: '',
  },
};

class Representative extends React.Component {
  constructor(props) {
    super(props);
    let legalEntity = {};
    this.state = assign(defaults, legalEntity);
  }

  componentDidMount() {
    this.fetchCarrierBillingData(this.props.carrier_id);
  }

  fetchCarrierBillingData = (carrier_id) => {
    this.setState({ loading: true });

    CustomersAPI.getBankAccounts(carrier_id)
      .then((res) => {
        const billingData = res.entity.data;
        const data = {
          ...defaults,
          type: billingData.business_type,
          business_name: billingData.business_profile.name,
          address: {
            line1: billingData.address.line1,
            line2: billingData.address.line2,
            city: billingData.address.city,
            state: billingData.address.state,
            postal_code: billingData.address.postal_code,
          },
        };

        this.setState({
          oldData: JSON.parse(JSON.stringify(data)),
          ...data,
        });
      })
      .catch((err) => {
        console.log(err.stack || err);
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  renderBillingInformationHeader = () => {
    const { t } = this.props;

    return (
      <div className="my-0 flex items-center justify-start">
        <h3 className="my-0 mr-1">{t('BILLING_INFORMATION')}</h3>
        {this.props.linkToStripeAccount}
      </div>
    );
  };

  renderBillingInformationForm = () => {
    const { t } = this.props;

    return (
      <div className="grid grid-cols-2 gap-x-4">
        <FormGroup>
          <label className="form-label">{t('FIRST_NAME')}</label>
          <FormControl
            value={this.state.first_name}
            onChange={(e) => this.setState({ first_name: e.target.value })}
          />
        </FormGroup>
        <FormGroup>
          <label className="form-label">{t('LAST_NAME')}</label>
          <FormControl
            value={this.state.last_name}
            onChange={(e) => this.setState({ last_name: e.target.value })}
          />
        </FormGroup>

        <FormGroup className="col-span-2">
          <label className="form-label">{t('DOB')}</label>
          <div className="flex">
            <div className="w-1/3">
              <FormControl
                placeholder="MM"
                value={this.state.dob.month}
                onChange={(e) => {
                  e.persist();
                  this.setState((prevState) => ({
                    dob: { ...prevState.dob, month: e.target.value },
                  }));
                }}
              />
            </div>
            <div className="w-1/3 px-4">
              <FormControl
                placeholder="DD"
                value={this.state.dob.day}
                onChange={(e) => {
                  e.persist();
                  this.setState((prevState) => ({
                    dob: { ...prevState.dob, day: e.target.value },
                  }));
                }}
              />
            </div>
            <div className="w-1/3">
              <FormControl
                placeholder="YYYY"
                value={this.state.dob.year}
                onChange={(e) => {
                  e.persist();
                  this.setState((prevState) => ({
                    dob: { ...prevState.dob, year: e.target.value },
                  }));
                }}
              />
            </div>
          </div>
        </FormGroup>
      </div>
    );
  };

  renderBillingInformationSummary = () => {
    const { t } = this.props;

    return (
      <div className="grid grid-cols-2 gap-4">
        <DescriptionListItem label={t('FIRST_NAME')}>
          {this.state.first_name || '-'}
        </DescriptionListItem>
        <DescriptionListItem label={t('LAST_NAME')}>
          {this.state.last_name || '-'}
        </DescriptionListItem>
        <DescriptionListItem label={t('DOB')} className="col-span-2">
          {getValidFormatMMDDYYYY(
            moment(
              `${this.state.dob.year}-${this.state.dob.month}-${this.state.dob.day}`,
              'YYYY-MM-DD',
            ),
          )}
        </DescriptionListItem>
      </div>
    );
  };

  renderAddressForm = () => {
    return (
      <div className="userDetailGroup userBillingAddress">
        <AddressForm
          address={this.normalizedAddress()}
          onChange={(address) => this.updateAddress(address)}
        />
      </div>
    );
  };

  renderAddressSummary = () => {
    const { t } = this.props;

    return (
      <div className="grid grid-cols-2 gap-4">
        <DescriptionListItem label={t('NAME')} className="col-span-2">
          {this.state.address.name || '-'}
        </DescriptionListItem>
        <DescriptionListItem
          label={t('STREET_ADDRESS_1')}
          className="col-span-2"
        >
          {this.state.address.line1 || '-'}
        </DescriptionListItem>
        <DescriptionListItem
          label={capitalize(t('CITY'))}
          className="col-span-2"
        >
          {this.state.address.city || '-'}
        </DescriptionListItem>
        <DescriptionListItem label={capitalize(t('STATE'))}>
          {this.state.address.state || '-'}
        </DescriptionListItem>
        <DescriptionListItem label={t('ZIP_CODE')}>
          {this.state.address.postal_code || '-'}
        </DescriptionListItem>
      </div>
    );
  };

  onCancel = () => {
    this.setState((prevState) => {
      const data = JSON.parse(JSON.stringify(prevState.oldData));

      return {
        ...data,
        oldData: prevState.oldData,
      };
    });
  };

  render() {
    const { t } = this.props;

    return (
      <EditableSection
        title={this.renderBillingInformationHeader()}
        canEdit={!this.state.loading && SessionStore.hasFinanceAdminRole()}
        onCancel={this.onCancel}
      >
        {(editing, saving, onSaveSuccess, onSaveFailed) => {
          if (saving) {
            const success = this.onSave();
            if (success) {
              onSaveSuccess();
            } else {
              onSaveFailed();
            }
          }

          if (this.state.loading) {
            return t('LOADING');
          }

          return (
            <>
              {this.renderErrors()}
              {!editing && this.renderBillingInformationSummary()}
              {editing && this.renderBillingInformationForm()}

              <hr />
              <h3 className="mb-4">{t('COMPANY_INFO')}</h3>
              {!editing && this.renderBusinessTypeSummary()}
              {editing && this.renderBusinessTypeForm()}

              <hr />
              <h3 className="mb-4">{t('BILLING_ADDRESS')}</h3>
              {!editing && this.renderAddressSummary()}
              {editing && this.renderAddressForm()}
            </>
          );
        }}
      </EditableSection>
    );
  }

  handleDismissAlert = () => {
    this.setState({ errors: [] });
  };

  renderErrors = () => {
    const { errors } = this.state;

    if (errors.length === 0) {
      return null;
    }
    return (
      <Alert bsStyle="danger" onDismiss={this.handleDismissAlert}>
        <ul>
          {errors.map((error, i) => (
            <li key={i}>
              {error.field}: {error.error}
            </li>
          ))}
        </ul>
      </Alert>
    );
  };

  renderPersonalIdNumber = () => {
    const { t } = this.props;
    let name = 'personal_id_number',
      label = t('SSN'),
      errorMessage = '',
      error = this.props.getError(name, label);
    if (error) {
      errorMessage = <div className="userDetailError">{error}</div>;
    }
    return (
      <FormGroup>
        <label className="form-label">{label}</label>
        <FormControl
          maxLength="12"
          data-validation={this.props.getFieldValidation(name)}
          type="text"
          className="userSSN"
          name={name}
          placeholder="XXX-XX-XXXX"
          value={this.state.personal_id_number}
          onChange={(e) => {
            this.setState({ personal_id_number: e.target.value });
          }}
        />
        {errorMessage}
      </FormGroup>
    );
  };

  renderBusinessTypeSummary = () => {
    const { t } = this.props;

    return (
      <div className="flex flex-col gap-4">
        <DescriptionListItem label={t('BUSINESS_TYPE')}>
          {t(BUSINESS_TYPE_SLUGS[this.state.type])}
        </DescriptionListItem>

        {this.state.type === BUSINESS_TYPES.COMPANY && (
          <>
            <DescriptionListItem label={t('COMPANY_NAME')}>
              {t(this.state.business_name) || '-'}
            </DescriptionListItem>
            <DescriptionListItem label={t('EIN')}>
              {t(this.state.business_tax_id) || '-'}
            </DescriptionListItem>
          </>
        )}

        {this.state.type === BUSINESS_TYPES.INDIVIDUAL && (
          <DescriptionListItem label={t('SSN')}>
            {t(this.state.personal_id_number) || '-'}
          </DescriptionListItem>
        )}
      </div>
    );
  };

  renderBusinessTypeForm = () => {
    const { t } = this.props;

    const CompanyTooltip = (
      <Tooltip id="tooltip-company-required-details">
        {t('LEGAL_ENTITY_VERIFICATION')}
      </Tooltip>
    );

    const IndividualTooltip = (
      <Tooltip id="tooltip-individual-required-details">
        {t('PERSON_IDENTIFICATION')}
      </Tooltip>
    );

    return (
      <div className="flex flex-col">
        <FormGroup>
          <label className="form-label">{t('BUSINESS_TYPE')}</label>
          <OverlayTrigger placement="top" overlay={CompanyTooltip}>
            <div className="userDetailValue">
              <Radio
                checked={this.state.type === BUSINESS_TYPES.COMPANY}
                name="type"
                id="user_type_company"
                value={BUSINESS_TYPES.COMPANY}
                onChange={this.onTypeChange}
                inline
              >
                {t(BUSINESS_TYPE_SLUGS[BUSINESS_TYPES.COMPANY])}
              </Radio>
            </div>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={IndividualTooltip}>
            <div className="userDetailValue">
              <Radio
                checked={this.state.type === BUSINESS_TYPES.INDIVIDUAL}
                onChange={this.onTypeChange}
                value={BUSINESS_TYPES.INDIVIDUAL}
                id="user_type_individual"
                inline
              >
                {t(BUSINESS_TYPE_SLUGS[BUSINESS_TYPES.INDIVIDUAL])}
              </Radio>
            </div>
          </OverlayTrigger>
        </FormGroup>

        {this.state.type === BUSINESS_TYPES.COMPANY && (
          <>
            <FormGroup>
              <label className="form-label">{t('COMPANY_NAME')}</label>
              <FormControl
                value={this.state.business_name}
                onChange={(e) => updateStateForField(e, 'business_name', this)}
              />
            </FormGroup>
            <FormGroup>
              <label className="form-label">{t('EIN')}</label>
              <FormControl
                value={this.state.business_tax_id}
                onChange={(e) =>
                  updateStateForField(e, 'business_tax_id', this)
                }
              />
            </FormGroup>
          </>
        )}

        {this.state.type === BUSINESS_TYPES.INDIVIDUAL &&
          this.renderPersonalIdNumber()}
      </div>
    );
  };

  updateAddress = (address) => {
    const updatedAddress = {
      city: address.city,
      line1: address.street,
      postal_code: address.zipcode,
      state: address.state,
      name: address.name,
    };
    this.setState((prevState) =>
      update(prevState, { $merge: { address: updatedAddress } }),
    );
  };

  normalizedAddress = () => {
    const address = this.state.address;
    return {
      city: address.city,
      street: address.line1,
      zipcode: address.postal_code,
      state: address.state,
      name: address.name,
    };
  };

  onTypeChange = (e) => {
    let value = e.target.value;
    this.setState({ type: value });
  };

  onSave = (e) => {
    e?.preventDefault();

    const errors = this.props.getValidationErrors();
    // Check company fields
    if (this.state.type === BUSINESS_TYPES.COMPANY) {
      ['business_name', 'business_tax_id'].forEach((field) => {
        const value = String(this.state[field] ?? '').trim();
        if (!value) {
          errors.push({
            field,
            value,
            error: 'Required for companies.',
          });
        }
      });
    }

    this.setState({ errors: errors });

    const value = assign({}, this.state, { errors: errors });

    delete value.oldData;

    if (this.props.onSave) {
      const success = this.props.onSave(value);

      if (success) {
        this.setState({ oldData: JSON.parse(JSON.stringify(value)) });
      }

      return success;
    }

    return !errors.length;
  };
}

export default withTranslation()(formMixin(validationMixin(Representative)));
